import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg } from '../../config/common';
import { useCloseOnClickOutside } from '../../hooks/useCloseOnClickOutside';
import { formatLocalDate } from '../../utils/dateUtils';
import { filterGamesByMode, GAME_MODE_PRACTICE, parseGameMode } from '../../utils/seasonType';
import ShotChart from '../../components/shotChart/ShotChart';
import { ShotChartRow } from '../../utils/shotLocation';

type GameFilterMode = 'all' | 'league' | 'custom';

const FILTER_TRIGGER_CLASS =
  'h-8 w-full rounded-lg border border-[#d8dee8] bg-white px-2.5 pr-7 text-left text-sm font-medium text-[#19133c] shadow-sm truncate focus:outline-none focus:ring-2 focus:ring-[#19133c]/10';

const SeasonShotsPage: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gamesMode = parseGameMode(sessionManager.getGamesMode());

  const [games, setGames] = useState<any[]>([]);
  const [gameFilterMode, setGameFilterMode] = useState<GameFilterMode>('all');
  const [customGameIds, setCustomGameIds] = useState<string[]>([]);
  const [isGameDropdownOpen, setIsGameDropdownOpen] = useState(false);
  const gameDropdownRef = useRef<HTMLDivElement>(null);
  const [rows, setRows] = useState<ShotChartRow[]>([]);
  const [loading, setLoading] = useState(true);

  // Restore the season's last-used game scope (shared with Teams/Players pages)
  useEffect(() => {
    const saved = sessionManager.getSeasonGameFilter();
    if (saved?.mode === 'league') setGameFilterMode('league');
    else if (saved?.mode === 'custom' && saved.customGameIds?.length) {
      setGameFilterMode('custom');
      setCustomGameIds(saved.customGameIds);
    } else {
      setGameFilterMode('all');
    }
  }, []);

  // Practice mode has no "league" concept
  useEffect(() => {
    if (gamesMode === GAME_MODE_PRACTICE && gameFilterMode === 'league') {
      setGameFilterMode('all');
    }
  }, [gamesMode, gameFilterMode]);

  useEffect(() => {
    if (!clientId || !seasonId) return;
    api
      .get(`${API_URLS.GET_GAMES_ALL}?clientId=${clientId}&seasonId=${seasonId}`)
      .then((res) => {
        if (res.data?.success) setGames(res.data.data || []);
      })
      .catch(() => {});
  }, [clientId, seasonId]);

  const fetchShotChart = useCallback(async () => {
    if (!clientId || !seasonId) return;
    setLoading(true);
    try {
      // Both sides — the chart's Us / Them toggle picks which one is drawn.
      const params: Record<string, string> = { clientId, seasonId, gameMode: gamesMode };
      if (gameFilterMode === 'league') {
        params.league = 'true';
      } else if (gameFilterMode === 'custom' && customGameIds.length > 0) {
        params.gameIds = customGameIds.join(',');
      }
      const response = await api.get(API_URLS.GET_SEASON_SHOT_CHART, { params });
      if (response.data?.success) {
        setRows(response.data.data.rows || []);
      } else {
        showErrorMsg(response.data?.message || 'Failed to load season shot chart');
      }
    } catch (err) {
      console.error('Error fetching season shot chart:', err);
      showErrorMsg('Failed to load season shot chart');
    } finally {
      setLoading(false);
    }
  }, [clientId, seasonId, gameFilterMode, customGameIds, gamesMode]);

  useEffect(() => {
    fetchShotChart();
  }, [fetchShotChart]);

  // Periods vary by game (quarters vs. halves) — derive the filter options from the data itself.
  const periods = useMemo(() => Array.from(new Set(rows.map((r) => r.period))).sort(), [rows]);

  // Everyone who took a shot in this scope, keyed by season player (game_players
  // ids differ per game, so they can't identify a player across the season).
  const players = useMemo(() => {
    const bySeasonId = new Map<string, { _id: string; no?: number | string; firstName: string; lastName: string }>();
    rows.forEach((r) => {
      if (r.player !== 'us' || !r.playerSeasonId || bySeasonId.has(r.playerSeasonId)) return;
      bySeasonId.set(r.playerSeasonId, {
        _id: r.playerSeasonId,
        no: r.playerNumber ?? undefined,
        firstName: r.playerName || '',
        lastName: '',
      });
    });
    return Array.from(bySeasonId.values()).sort((a, b) => Number(a.no ?? 999) - Number(b.no ?? 999));
  }, [rows]);

  // Practice seasons have no league games to choose from
  const selectableGames = useMemo(() => filterGamesByMode(games, gamesMode), [games, gamesMode]);

  const closeGameDropdown = useCallback(() => setIsGameDropdownOpen(false), []);
  useCloseOnClickOutside(gameDropdownRef, isGameDropdownOpen, closeGameDropdown);

  const formatGameOptionLabel = useCallback((game: any) => {
    const dateStr = game.date ? formatLocalDate(game.date, undefined, { month: 'numeric', day: 'numeric' }) : '';
    const opp = game.opponentName || game.opponent || 'Game';
    return `${dateStr} ${opp}`.trim();
  }, []);

  const selectedGameLabel = useMemo(() => {
    if (gameFilterMode === 'custom') {
      if (customGameIds.length > 1) return `${customGameIds.length} Games Selected`;
      if (customGameIds.length === 1) {
        const game = selectableGames.find((g: any) => String(g._id) === customGameIds[0]);
        return game ? formatGameOptionLabel(game) : '1 Game Selected';
      }
      return 'Choose Games';
    }
    return gameFilterMode === 'league' ? 'League Games' : 'All Games';
  }, [customGameIds, formatGameOptionLabel, gameFilterMode, selectableGames]);

  const selectGamePreset = (mode: 'all' | 'league') => {
    setGameFilterMode(mode);
    setCustomGameIds([]);
    setIsGameDropdownOpen(false);
    sessionManager.setSeasonGameFilter({ mode });
  };

  const toggleCustomGame = (id: string) => {
    setGameFilterMode('custom');
    setCustomGameIds((prev) => {
      const next = prev.includes(id) ? prev.filter((g) => g !== id) : [...prev, id];
      sessionManager.setSeasonGameFilter({ mode: 'custom', customGameIds: next });
      return next;
    });
  };

  const gamePicker = (
    <div className="relative w-full shrink-0 sm:w-56" ref={gameDropdownRef}>
      <button type="button" className={FILTER_TRIGGER_CLASS} onClick={() => setIsGameDropdownOpen((prev) => !prev)}>
        {selectedGameLabel}
      </button>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-[#8a93a6]">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      {isGameDropdownOpen && (
        <div className="absolute left-0 mt-1 z-50 w-72 max-w-[calc(100vw-2rem)] max-h-72 overflow-y-auto rounded-xl border border-[#e6ebf2] bg-white shadow-lg p-1.5">
          <button
            type="button"
            className="block w-full rounded-lg px-2.5 py-1.5 min-h-8 text-left text-sm font-medium text-[#19133c] hover:bg-[#f4f6fa]"
            onClick={() => selectGamePreset('all')}
          >
            All Games
          </button>
          {gamesMode !== GAME_MODE_PRACTICE && (
            <button
              type="button"
              className="block w-full rounded-lg px-2.5 py-1.5 min-h-8 text-left text-sm font-medium text-[#19133c] hover:bg-[#f4f6fa]"
              onClick={() => selectGamePreset('league')}
            >
              League Games
            </button>
          )}
          <div className="my-1 border-t border-[#eef1f6]" />
          {selectableGames.length === 0 && <span className="block px-2.5 py-1.5 text-sm text-[#8a93a6]">No games found.</span>}
          {selectableGames.map((g: any) => {
            const gid = String(g._id);
            return (
              <label
                key={gid}
                className="flex min-h-8 cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium text-[#19133c] hover:bg-[#f4f6fa]"
              >
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 shrink-0 rounded border-[#c5ccd8]"
                  checked={gameFilterMode === 'custom' && customGameIds.includes(gid)}
                  onChange={() => toggleCustomGame(gid)}
                />
                <span className="truncate">{formatGameOptionLabel(g)}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight text-[#19133c] flex items-center gap-2">
        Season Shot Chart
        <span className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-[#19133c] bg-[#7fe526]">
          BETA
        </span>
      </h1>
      <ShotChart
        rows={rows}
        loading={loading}
        periods={periods}
        players={players}
        playerFilterKey="playerSeasonId"
        mode="season"
        showSideToggle
        leadingControls={gamePicker}
        emptyMessage="No located shots for this game scope yet."
      />
    </div>
  );
};

export default SeasonShotsPage;
